import {
  Color,
  CompositeLayer,
  CompositeLayerProps,
  LayerContext,
  Position,
  UpdateParameters,
  PickingInfo,
  Layer,
} from "@deck.gl/core";
import { TextLayer, ScatterplotLayer, PathLayer } from "@deck.gl/layers";
import { PathStyleExtension } from "@deck.gl/extensions";
import { Polygon, MultiPolygon, Point, Feature, BBox } from "@turf/helpers";
import { bboxPolygon, polygonSmooth } from "@turf/turf";
import { identity } from "lodash";

import { BaseMapType } from "src/js/stores/map/store";
import { LogEventProps } from "react-migration/lib/util/logEvent";
import { logEventV2 } from "react-migration/lib/util/logEventV2";
import { cellAddressToTileBBox } from "@landtechnologies/search-layer-contracts";

const SINGLE_ITEM_CLUSTER_GOOGLE_ZOOM_LEVEL = 16;
const FORCE_EXPLODE_PRECISION_LEVEL = 20;
const CLUSTER_CLICK_ZOOM_LEVEL_INCREASE = 1;
const MAX_GOOGLE_ZOOM = 20;
const MIN_GOOGLE_ZOOM = 7;
const CLUSTER_EXPLOSION_THRESHOLD = 100;
const CLUSTER_AREA_TRANSITION_MS = 100;

type UnparsedOpenSearchDocument = Record<string, unknown>;
type OnClickEvent = Parameters<NonNullable<CompositeLayerProps["onClick"]>>[1];

type HasGeometry = {
  geometry: Polygon | MultiPolygon | Point;
};

type RawClusterPoint<D extends HasGeometry, A extends Record<string, unknown>> = A & {
  cell: string;
  count: number;
  results: D[];
  centroid: {
    location: {
      lat: number;
      lon: number;
    };
    count: number;
  };
};

export type ParsedClusterPoint<
  D extends HasGeometry = HasGeometry,
  A extends Record<string, unknown> = Record<string, unknown>
> = RawClusterPoint<D, A> & {
  centroidCoordinates: Position;
  cellCoordinates: Position;
  cellClusterArea: Polygon;
};

export type ClusteredViewportSearchMapLayerProps<
  D extends HasGeometry,
  P extends Record<string, unknown> = Record<string, unknown>,
  A extends Record<string, unknown> = Record<string, never>,
  F extends Record<string, unknown> = Record<string, unknown>
> = CompositeLayerProps & {
  renderFeatureLayers: (features: Feature<Point | Polygon | MultiPolygon, P>[]) => Layer | Layer[];

  filters: F;
  googleMap: google.maps.Map | null;
  mapType?: BaseMapType;
  url: string;
  parseOpenSearchFeatures?: (
    d: UnparsedOpenSearchDocument
  ) => Feature<Point | Polygon | MultiPolygon, P>[] | Feature<Point | Polygon | MultiPolygon, P>;
  getClusterFillColor: Color | ((d: ParsedClusterPoint<D, A>) => Color);
  getClusterRadius: number | ((d: ParsedClusterPoint<D, A>) => number);
  onFetchClusterError?: (error: Error) => void;
  clusterEventProperties: {
    selectionLayerId: string;
  } & LogEventProps;
};

export class ClusteredViewportSearchMapLayer<
  /** Raw OpenSearch document returned as a result item */
  D extends HasGeometry,
  /** Properties of the feature parsed from D */
  P extends Record<string, unknown> = Record<string, unknown>,
  /** Aggregations of the features parsed from D */
  A extends Record<string, unknown> = Record<string, never>,
  /** Filters to apply to the query */
  F extends Record<string, unknown> = Record<string, unknown>
> extends CompositeLayer<ClusteredViewportSearchMapLayerProps<D, P, A, F>> {
  static layerName = "ClusteredViewportSearchMapLayer";

  declare state: {
    /** Returned clusters after they are parsed by parseClusterPoint and parseOpenSearchFeatures */
    clusters: ParsedClusterPoint<D, A>[];
    /** Cluster features that have been exploded into individual features to be rendered by renderFeatureLayers */
    explodedFeatures: Feature<Point | Polygon | MultiPolygon, P>[];
    /** Whether the layer is currently loading */
    loading: boolean;
    /** Used to abort in flight cluster requests if viewport changes */
    abortController: AbortController | null;
    /** The timeout for the current request. Used to debounce cluster requests */
    fetchTimeout: number | null;
    /** The cell address of the current hovered cluster */
    hoveredClusterCell: string | null;
  };

  constructor(props: ClusteredViewportSearchMapLayerProps<D, P, A, F>) {
    super({
      ...props,
      onClick: (...args) => this.onClickHandler(...args),
      onHover: (...args) => this.onHoverHandler(...args),
    });
  }

  initializeState() {
    this.setState({
      clusters: [],
      explodedFeatures: [],
      abortController: null,
      fetchTimeout: null,
      loading: false,
      hoveredClusterCell: null,
    });
  }

  override shouldUpdateState({
    changeFlags,
  }: UpdateParameters<Layer<ClusteredViewportSearchMapLayerProps<D, P, A>>>): boolean {
    return changeFlags.propsOrDataChanged || changeFlags.viewportChanged;
  }

  override finalizeState(context: LayerContext) {
    super.finalizeState(context);

    this.state.clusters = [];
    this.state.explodedFeatures = [];

    if (this.state.fetchTimeout) {
      window.clearTimeout(this.state.fetchTimeout);
    }

    if (this.state.abortController) {
      this.state.abortController.abort();
      this.state.abortController = null;
    }
  }

  updateState({ changeFlags, oldProps, props }: UpdateParameters<this>) {
    if (!props.visible) {
      return;
    }

    if (changeFlags.viewportChanged || (changeFlags.propsChanged && oldProps.filters !== props.filters)) {
      this.debouncedFetchClusters();
    }
  }

  private calculateClusterFeatures(rawClusterData: RawClusterPoint<D, A>[], precision: number) {
    const { parseOpenSearchFeatures = identity } = this.props;
    const explodeAll = precision >= FORCE_EXPLODE_PRECISION_LEVEL;
    const parsedClusters = rawClusterData.map(parseClusterPoint);

    const unexplodedClusters = explodeAll
      ? []
      : parsedClusters.filter(({ count, results }) => count >= CLUSTER_EXPLOSION_THRESHOLD || !results?.length);

    const unexplodedCells = unexplodedClusters.map((cluster) => cluster.cell);
    const explodedClusters = parsedClusters.filter((cluster) => !unexplodedCells.includes(cluster.cell));
    const explodedFeatures = explodedClusters
      .flatMap((cluster) => cluster!.results ?? [])
      .flatMap((hit) => parseOpenSearchFeatures(hit));

    return {
      clusters: unexplodedClusters,
      explodedFeatures,
    };
  }

  private isClusterInteraction(info: PickingInfo<ParsedClusterPoint<D, A>>): boolean {
    const sourceLayerId = info?.sourceLayer?.id || "";
    return sourceLayerId.endsWith("clusters");
  }

  private preventEventBubbling(event: OnClickEvent) {
    // @ts-expect-error incorrect typings
    event.srcEvent.domEvent?.preventDefault?.();
  }

  private onClickHandler(info: PickingInfo<ParsedClusterPoint<D, A>>, event: OnClickEvent) {
    if (!info.object) {
      return;
    }

    if (this.isClusterInteraction(info)) {
      this.preventEventBubbling(event);
      this.handleClusterClick(info.object);
    }
  }

  private onHoverHandler(info: PickingInfo<ParsedClusterPoint<D, A>>, event: OnClickEvent) {
    if (this.isClusterInteraction(info)) {
      this.preventEventBubbling(event);
      this.setState({ hoveredClusterCell: info.object ? info.object.cell : null });
    }
  }

  private handleClusterClick(cluster: ParsedClusterPoint<D, A>) {
    const { googleMap } = this.props;

    if (googleMap && cluster) {
      const isSingleItemCluster = cluster.count === 1;
      const deckGlZoom = Math.round(this.context.viewport.zoom);

      const [lng, lat] = isSingleItemCluster ? cluster.centroidCoordinates : cluster.cellCoordinates;

      const newZoom = isSingleItemCluster
        ? SINGLE_ITEM_CLUSTER_GOOGLE_ZOOM_LEVEL
        : deckGlZoom + CLUSTER_CLICK_ZOOM_LEVEL_INCREASE;

      const newGoogleZoom = newZoom + 1;

      googleMap.setCenter(new google.maps.LatLng(lat, lng));
      googleMap.setZoom(constrainZoom(newGoogleZoom));
      this.setState({ hoveredClusterCell: null });

      logEventV2({
        name: "Bundle Map Click",
        properties: {
          ...this.props.clusterEventProperties,
          clickMethod: "left",
          action: "cluster_click",
          zoom: newGoogleZoom,
          clusterAddress: cluster.cell,
        },
      });
    }
  }

  private debouncedFetchClusters() {
    if (this.state.fetchTimeout) {
      window.clearTimeout(this.state.fetchTimeout);
    }

    const timeout = window.setTimeout(() => this.fetchClusters(), 500);

    this.state.fetchTimeout = timeout;
  }

  private async fetchClusters() {
    if (this.state.abortController) {
      this.state.abortController.abort();
    }

    const abortController = new AbortController();
    this.state.abortController = abortController;

    const bounds = this.context.viewport.getBounds();
    const [west, south, east, north] = bounds;
    const precision = Math.round(this.context.viewport.zoom) + 1;
    const { url, filters, onFetchClusterError } = this.props;

    try {
      this.setState({ loading: true });

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          north,
          south,
          east,
          west,
          precision,
          filters,
        }),
        signal: abortController.signal,
      });
      const data: RawClusterPoint<D, A>[] = await response.json();

      this.setState({
        ...this.calculateClusterFeatures(data, precision),
        loading: false,
      });
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") {
        this.state.abortController = null;
        return;
      }

      onFetchClusterError?.(error as Error);

      this.setState({
        ...this.calculateClusterFeatures([], precision),
        loading: false,
      });
    }
  }

  /** Returns true if all async resources are loaded */
  get isLoaded(): boolean {
    // Track the explicit loading done by this layer
    return !this.state.loading && super.isLoaded;
  }

  renderLayers() {
    const { clusters, explodedFeatures, hoveredClusterCell } = this.state;

    const { renderFeatureLayers, mapType } = this.props;
    const { getClusterFillColor, getClusterRadius } = this.props;

    const featureLayersResult = renderFeatureLayers(explodedFeatures);
    const featureLayers = Array.isArray(featureLayersResult) ? featureLayersResult : [featureLayersResult];

    const isDarkMapStyle = mapType === "satellite" || mapType === "hybrid";

    return [
      ...featureLayers,
      new PathLayer<ParsedClusterPoint<D, A>>(
        this.getSubLayerProps({
          id: `${this.props.id}-clusters-grid`,
          data: clusters,
          pickable: false,
          widthUnits: "pixels",
          getWidth: 4,
          getPath: (d: ParsedClusterPoint<D, A>) => d.cellClusterArea?.coordinates[0],
          getColor: (d: ParsedClusterPoint<D, A>) =>
            d.cell === hoveredClusterCell ? [255, 255, 255, 255 * (isDarkMapStyle ? 0.5 : 1)] : [255, 255, 255, 0],
          updateTriggers: {
            getColor: [mapType, hoveredClusterCell],
          },
          getDashArray: [6, 4],
          dashJustified: true,
          extensions: [new PathStyleExtension({ dash: true })],
          transitions: {
            getColor: CLUSTER_AREA_TRANSITION_MS,
          },
        })
      ),
      new ScatterplotLayer<ParsedClusterPoint<D, A>>(
        this.getSubLayerProps({
          id: `${this.props.id}-clusters`,
          data: clusters,
          getRadius: getClusterRadius,
          getFillColor: getClusterFillColor,
          getPosition: (d: ParsedClusterPoint<D, A>) => d.centroidCoordinates,
          pickable: true,
          opacity: 1,
          stroked: true,
          filled: true,
          radiusUnits: "pixels",
          lineWidthUnits: "pixels",
          getLineWidth: 3,
          getLineColor: [255, 255, 255, 255],
        })
      ),
      new TextLayer<ParsedClusterPoint<D, A>>(
        this.getSubLayerProps({
          id: `${this.props.id}-clusters-text`,
          data: clusters,
          pickable: false,
          getPosition: (d: ParsedClusterPoint<D, A>) => d.centroidCoordinates,
          getText: getClusterCountDisplay,
          getColor: [255, 255, 255],
          getSize: 16,
          fontWeight: 500,
          getTextAnchor: "middle",
          getAlignmentBaseline: "center",
          fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
        })
      ),
    ];
  }
}

function parseClusterPoint<D extends HasGeometry, A extends Record<string, unknown>>(
  rawClusterPoint: RawClusterPoint<D, A>
): ParsedClusterPoint<D, A> {
  const cellCoordinates = cellAddressToCoords(rawClusterPoint.cell);
  const cellBBox = cellAddressToTileBBox(rawClusterPoint.cell) as BBox;

  return {
    ...rawClusterPoint,
    cellCoordinates,
    cellClusterArea: polygonSmooth(bboxPolygon(cellBBox), {
      iterations: 3,
    }).features[0].geometry,
    centroidCoordinates: [rawClusterPoint.centroid.location.lon, rawClusterPoint.centroid.location.lat],
  };
}

function getClusterCountDisplay({ count }: ParsedClusterPoint) {
  if (count < 1000) {
    return count.toString();
  }
  if (count < 10000) {
    return (count / 1000).toFixed(1) + "k";
  }
  if (count < 1000000) {
    return Math.round(count / 1000) + "k";
  }
  return (count / 1000000).toFixed(1) + "m";
}

function constrainZoom(zoom: number) {
  return Math.max(Math.min(zoom, MAX_GOOGLE_ZOOM), MIN_GOOGLE_ZOOM);
}

function cellAddressToCoords(cellAddress: string): Position {
  const [z, x, y] = cellAddress.split("/").map(Number);

  // Calculate the center of the tile
  const lng = ((x + 0.5) / Math.pow(2, z)) * 360 - 180;
  const lat = (Math.atan(Math.sinh(Math.PI * (1 - (2 * (y + 0.5)) / Math.pow(2, z)))) * 180) / Math.PI;

  return [lng, lat];
}
